import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { BlindData, GalleryViewType, GalleryViewTypes, Opening } from '@root/app.interfaces';
import { IS_PROD, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { GTM_OVERVIEW_EVENTS } from '@core/services/gtm/gtm.events';
import { GtmService } from '@core/services/gtm/gtm.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { Router } from '@angular/router';
import { DeleteModalComponent } from '@shared/modals/delete-modal/delete-modal.component';
import { ModalService } from '@core/services/modal/modal.service';
import { SaveSessionBlindsService } from '@core/services/save-session-blinds/save-session-blinds.service';
import { ScrollService } from '@core/services/scroll/scroll.service';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { QuizService } from '@core/services/quiz/quiz.service';

@Component({
	selector: 'app-blind-actions',
	templateUrl: './blind-actions.component.html',
	styleUrls: ['./blind-actions.component.scss', './blind-actions.responsive.scss'],
})
export class BlindActionsComponent {
	@HostBinding('class') get hostClass() {
		return [this.viewType, this.IS_PROD ? 'production' : ''];
	}
	@Input() blind: BlindData;
	@Input() viewType: GalleryViewType = GalleryViewTypes.Default;
	@Input() showIVButton = true;

	@Output() edit = new EventEmitter<BlindData>();
	@Output() delete = new EventEmitter<BlindData>();
	@Output() imageVisualisation = new EventEmitter<BlindData>();

	protected readonly IS_PROD = IS_PROD;

	constructor(
		private readonly gtm: GtmService,
		private readonly router: Router,
		private readonly modalService: ModalService,
		private readonly saveSessionBlind: SaveSessionBlindsService,
		private readonly scrollService: ScrollService,
		private readonly countryCodeService: SetupPageService,
		private readonly shareService: ShareService,
		private readonly sessionStorageService: SessionStorageService,
		private readonly quizService: QuizService,
	) {}

	onImageVisualisation(): void {
		this.scrollService.scrollToTopAsync().then(() => {
			this.sessionStorageService.setSession(this.blind.blind_id, STORAGE_NAMES.zip_current_blind_id);
			this.sessionStorageService.setSession(this.blind.type, STORAGE_NAMES.zip_blind_type);
			this.sessionStorageService.setSession(true, STORAGE_NAMES.zip_image_visualisation_status);
			this.sessionStorageService.removeData(STORAGE_NAMES.zip_last_opened_blind_id);

			this.imageVisualisation.emit(this.blind);
			void this.redirectToHomePage();
		});

		this.gtm.pushEvent(GTM_OVERVIEW_EVENTS.IV_BLIND);
	}

	onEdit(): void {
		this.sessionStorageService.setSession(this.blind.blind_id, STORAGE_NAMES.zip_current_blind_id);
		this.sessionStorageService.setSession(this.blind.type, STORAGE_NAMES.zip_blind_type);
		this.sessionStorageService.removeData(STORAGE_NAMES.zip_last_opened_blind_id);

		void this.redirectToHomePage();

		this.gtm.pushEvent(GTM_OVERVIEW_EVENTS.EDIT_BLIND);
		this.edit.emit(this.blind);
	}

	onDelete(): void {
		const { blind_id } = this.blind;
		const dialogRef = this.modalService.openResponsiveDialog({
			component: DeleteModalComponent,
			...this.modalService.getConfig(DeleteModalComponent.ID),
		});

		dialogRef.afterClosed().subscribe(async (status) => {
			if (status) {
				this.deleteBlindFromStorage(blind_id);
				this.onDeselectOpenings(blind_id);
				await this.updateSession();
				this.redirectIfNoBlinds();
				this.delete.emit(this.blind);
			}
		});

		this.gtm.pushEvent(GTM_OVERVIEW_EVENTS.DELETE_BLIND);
	}

	deleteBlindFromStorage(blindId: number): void {
		const storageData: BlindData[] = (this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data) || []).filter(
			(blind: BlindData) => blind.has_name,
		);
		const deletedBlindIndex = storageData.findIndex((x) => x.blind_id === blindId);

		if (deletedBlindIndex >= 0) {
			if (deletedBlindIndex < storageData.length - 1) {
				storageData.forEach((x, i) => (x.name = i > deletedBlindIndex && x.name === `Blind ${i + 1}` ? `Blind ${i}` : x.name));
			}

			storageData.splice(deletedBlindIndex, 1);
			this.sessionStorageService.setBlindData(storageData, STORAGE_NAMES.zip_blind_data);
		}
	}

	async redirectToHomePage(): Promise<void> {
		await this.router.navigateByUrl('home');
		if (this.sessionStorageService.getSession(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.design) {
			this.quizService.showQuizIfNeeded(VIEW_TYPES.design);
		}
	}

	redirectIfNoBlinds(): void {
		const storageData: BlindData[] = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		if (!storageData?.length) {
			this.scrollService.scrollToTop('auto');
			void this.router.navigateByUrl('home');

			this.sessionStorageService.removeData(STORAGE_NAMES.zip_image_visualisation_status);
			this.sessionStorageService.removeBlindData(STORAGE_NAMES.zip_blind_data);
			this.countryCodeService.setWelcomeModal();
			this.shareService.setBlindEmpty(true);
		}
	}

	async updateSession(): Promise<void> {
		const sessionSaved = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_session_saved);

		if (sessionSaved) {
			await this.saveSessionBlind.PutStorageDataToServerPromise();
		}
	}

	onDeselectOpenings(blindId: number): void {
		// Deselect the opening that corresponds to the deleted blind
		const currentOpenings = this.sessionStorageService.getSession(STORAGE_NAMES.zip_openings_data);
		currentOpenings.map((opening: Opening) => {
			if (opening.blindId === blindId) {
				opening.isFitted = false;
				opening.isSelected = false;
			}
		});
		this.sessionStorageService.removeData(STORAGE_NAMES.zip_openings_data);
		this.sessionStorageService.setSession(currentOpenings, STORAGE_NAMES.zip_openings_data);
	}
}
